import React, { act, Component } from 'react'
import { Popover, PopoverBody, PopoverHeader, Button, Input, Progress, Alert } from 'reactstrap'
import { graphql } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import Select from 'react-select'
import moment from 'moment'
import { RepsTypeAhead } from '../../TaskManagement/TaskComponents/TypeAheadComponents'

import withApolloClient from '../../withApolloClient'
import DatetimePickerWithTimeZone from "../../Material/DatetimePickerWithTimeZone"
import { PreferredDateTimeContext } from "../../Context/PreferredDateTimeContext"

const UpdateIncidentMutation = gql`
mutation UpdateIncidentMutation($input: UpdateIncidentMutationInput!) {
  updateIncident(input: $input) {
    ok
    errors {
      messages
    }
  }
}`

const IncidentReportedByChoices = gql`
query IncidentReportedByChoices {
  allIncidentChoices: __type(name: "IncidentsIncidentReportedByChoices") {
    values: enumValues {
      name
      description
    }
  }
}`

const AllCarRentals = gql`query AllCarRentals($carPk: Int!, $activeOn:Date!){
    allRentals(carPk: $carPk, activeOn:$activeOn) {
        edges {
            node {
                id
                startDate
                endDate
                driver {
                    id
                    pk
                    name
                }
            }
        }
    } 
}`

class UpdateIncidentInfo extends Component {
    constructor(props) {
        super(props)
        this.state = {
            value: props.value || '',
            error: null,
            loading: false
        }
    }

    handleUpdateIncident = () => {
        const { apolloClient, incidentId, name, refetchIncident, handleClose, metadata, type } = this.props
        const { value } = this.state

        this.setState({ loading: true })
        let input = { incidentId }

        if (metadata || type === "json") {
            const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {})
            const additionalInfo = parsed.additional_info || {}
            const updatedAdditionalInfo = { ...additionalInfo, [name]: value }
            const updatedMetadata = { ...parsed, additional_info: updatedAdditionalInfo }
            input["metadata"] = JSON.stringify(updatedMetadata)
        } else {
            input[name] = value
        }

        apolloClient.mutate({
            mutation: UpdateIncidentMutation,
            variables: { input }
        }).then(response => {
            if (response.data.updateIncident.ok) {
                refetchIncident()
                handleClose()
                this.setState({ value: '', loading: false, error: null })
            } else {
                const msg = response.data.updateIncident.errors[0]?.messages.toString() || 'Update failed'
                this.setState({ error: msg, loading: false })
            }
        }).catch(() => {
            this.setState({ loading: false, error: 'An error occurred. Please try again.' })
        })
    }

    handleDeleteIncidentField = () => {
        const { apolloClient, incidentId, name, refetchIncident, handleClose, metadata } = this.props

        this.setState({ loading: true })

        const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {})
        const additionalInfo = parsed.additional_info || {}
        delete additionalInfo[name]

        const updatedMetadata = { ...parsed, additional_info: additionalInfo }

        const input = {
            incidentId,
            metadata: JSON.stringify(updatedMetadata)
        }

        apolloClient.mutate({
            mutation: UpdateIncidentMutation,
            variables: { input }
        }).then(response => {
            if (response.data.updateIncident.ok) {
                refetchIncident()
                handleClose()
                this.setState({ value: '', loading: false, error: null })
            } else {
                const msg = response.data.updateIncident.errors[0]?.messages.toString() || 'Delete failed'
                this.setState({ error: msg, loading: false })
            }
        }).catch(() => {
            this.setState({ loading: false, error: 'An error occurred while deleting. Please try again.' })
        })
    }


    render() {
        const { open, target, handleClose, title, name, allIncidentChoices, type, min, max } = this.props
        const { value, loading, error } = this.state

        const choices = allIncidentChoices?.values ? allIncidentChoices.values.map(item => ({ value: item.name, label: item.description })) : []
        const inputFieldConfig = this.props.incidentMetadataColumnConfigs?.find(f => f.key === name)

        const driverSelectOptions = (this.props.rentals?.edges || []).map(({ node }) => ({
            value: node.id,
            pk: node.driver.pk ?? node.driver.id,
            name: node.driver.name || 'Unknown',
            start: moment(node.startDate).format('YYYY-MM-DD'),
            end: node.endDate ? moment(node.endDate).format('YYYY-MM-DD') : "ACTIVE",
        }))
        return (
            <Popover placement="auto-start" isOpen={open} target={target} toggle={handleClose} trigger="legacy" style={{ minWidth: '250px' }}>
                <PopoverHeader onClick={handleClose}>{title}&nbsp;&nbsp;<i className="fa fa-times-circle float-right" /></PopoverHeader>
                <PopoverBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    <div className="pb-2">
                        {name === 'notificationDatetime' ? (
                            <PreferredDateTimeContext.Consumer>
                                {({ preferredDateTimeInfo }) => (
                                    <DatetimePickerWithTimeZone
                                        type={type || 'datetime-local'}
                                        name={name}
                                        timezone={preferredDateTimeInfo.timezone}
                                        value={value}
                                        min={min ? moment(min).tz(preferredDateTimeInfo.timezone).format(moment.HTML5_FMT.DATETIME_LOCAL) : null}
                                        max={max ? moment(max).tz(preferredDateTimeInfo.timezone).format(moment.HTML5_FMT.DATETIME_LOCAL) : null}
                                        setError={(error) => this.setState({ error })}
                                        updateInput={(name, value) => this.setState({ value: value })}
                                    />
                                )}
                            </PreferredDateTimeContext.Consumer>
                        ) :
                            name === 'assignedToIds' ? (
                                <RepsTypeAhead onChange={(value) => this.setState({ value: value })} defaultValue={value} permissions={["custom_can_assign_to_incident"]} />
                            ) :
                                name === 'rentalId' ? (
                                    <Select
                                        className="bos-custom-select"
                                        classNamePrefix="bos-select"
                                        options={driverSelectOptions}
                                        value={driverSelectOptions?.find(opt => opt.value === (this.state.value || this.props.value)) || null}
                                        formatOptionLabel={(opt) => (<><p className="m-0"><small>({opt.pk})</small> {opt.name}</p><small>{opt.start} — {opt.end}</small></>)}
                                        placeholder="Select Driver"
                                        isLoading={this.props.carLoading}
                                        onChange={(opt) => this.setState({ value: opt?.value || '' })}
                                    />
                                ) :
                                    name === 'reportedBy' ? (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            options={choices}
                                            defaultValue={choices.find(opt => opt.value === value)}
                                            onChange={(opt) => this.setState({ value: opt.value })}
                                        />
                                    ) : type === "json" && inputFieldConfig?.dataType === 'enum' ? (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            options={inputFieldConfig.options.map(opt => ({ value: opt, label: opt }))}
                                            defaultValue={{ value: value, label: value }}
                                            onChange={(opt) => this.setState({ value: opt.value })}
                                        />
                                    ) : type === "json" && inputFieldConfig?.dataType === 'bool' ? (
                                        <div className="d-flex align-items-center">
                                            <span >Yes</span>
                                            <div className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    className="toggle-switch-checkbox"
                                                    id={`bool-toggle-${name}`}
                                                    checked={!value}
                                                    onChange={(e) => this.setState({ value: !e.target.checked })}
                                                />
                                                <label className="toggle-switch-label" htmlFor={`bool-toggle-${name}`}>
                                                    <span className="toggle-switch-inner" />
                                                    <span className="toggle-switch-switch" />
                                                </label>
                                            </div>
                                            <span >No</span>
                                        </div>
                                    ) :
                                        (<Input
                                            type={type || 'text'}
                                            name={name}
                                            value={value}
                                            onChange={(e) => this.setState({ value: e.target.value })}
                                        />
                                        )}

                        {loading ? (
                            <Progress animated color="info" value={100} />
                        ) : (
                            <div className="d-flex justify-content-between mt-3">
                                {this.props.showDelete && (
                                    <Button color="danger" size="sm" onClick={this.handleDeleteIncidentField}> Delete </Button>
                                )}
                                <Button color="primary" size="sm" onClick={this.handleUpdateIncident}> Submit</Button>
                            </div>
                        )}
                    </div>
                </PopoverBody>
            </Popover>
        )
    }
}

export default graphql(IncidentReportedByChoices, {
    options: { fetchPolicy: 'cache-first' },
    props: ({ data: { allIncidentChoices } }) => ({ allIncidentChoices }),
})(
    graphql(AllCarRentals, {
        options: ({ carPk, incidentDate }) => ({
            variables: { carPk, activeOn: incidentDate },
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            skip: !carPk || !incidentDate,
        }),
        props: ({ data }) => ({
            rentals: data?.allRentals,
            carLoading: data?.loading,
        }),
    })(withApolloClient(UpdateIncidentInfo))
)